<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  Play,
  Pause,
  RefreshCw,
  Activity,
  Plus,
} from 'lucide-vue-next'

import Card from '@/components/ui/Card.vue'
import Badge from '@/components/ui/Badge.vue'
import Button from '@/components/ui/Button.vue'
import StatsCard from '@/components/dashboard/StatsCard.vue'
import SpeedChart from '@/components/dashboard/SpeedChart.vue'
import RecentCompleted from '@/components/dashboard/RecentCompleted.vue'
import RecentErrors from '@/components/dashboard/RecentErrors.vue'
import AddTaskDialog from '@/components/tasks/AddTaskDialog.vue'
import ClusterStatusCard from '@/components/dashboard/ClusterStatusCard.vue'
import HealthCard from '@/components/dashboard/HealthCard.vue'

import { useSpeedHistory } from '@/composables/useSpeedHistory'
import { onSessionCleared } from '@/stores/auth'
import { getGlobalStat, type GlobalStat } from '@/api/global'
import {
  countCompletedTasks,
  listRecentCompleted,
  listRecentErrors,
  type CompletedTask,
} from '@/api/stopped'
import { getClusterSummary, type ClusterSummary } from '@/api/clusterSummary'
import { getBTStats, type BTStats } from '@/api/rpc'
import { formatBytes } from '@/composables/useFormat'
import { formatSpeed } from '@/utils/format'

const { t } = useI18n()
const router = useRouter()
const showAddDialog = ref(false)

// --- live speed window (1s sampling, 60s window, WS-driven) -----------------
const {
  history,
  current,
  paused,
  wsConnected,
  toggle,
  refresh: refreshSpeed,
} = useSpeedHistory({ windowSize: 60, intervalMs: 1000 })

// --- stat cards (1.5s refresh, independent of the chart) --------------------
const stat = ref<GlobalStat | null>(null)
const statLoading = ref(true)
let statTimer: number | null = null

async function loadStat(): Promise<void> {
  try {
    stat.value = await getGlobalStat()
  } catch {
    /* client.ts already surfaced a toast */
  } finally {
    statLoading.value = false
  }
}

/**
 * Card values prefer the WS-derived numbers when the socket is live (they are
 * fresher than the 1.5s poll), and fall back to the polled stat otherwise.
 */
const cardStat = computed<GlobalStat | null>(() => {
  if (wsConnected.value && current.value) return current.value
  return stat.value ?? current.value
})

const downTrend = computed(() => history.value.slice(-20).map((p) => p.down))
const upTrend = computed(() => history.value.slice(-20).map((p) => p.up))

const cards = computed(() => [
  {
    id: 'down',
    label: t('dashboard.downloadSpeed'),
    value: formatSpeed(cardStat.value?.downloadSpeed ?? 0),
    icon: ArrowDownToLine,
    accent: 'blue' as const,
    trend: downTrend.value,
  },
  {
    id: 'up',
    label: t('dashboard.uploadSpeed'),
    value: formatSpeed(cardStat.value?.uploadSpeed ?? 0),
    icon: ArrowUpFromLine,
    accent: 'green' as const,
    trend: upTrend.value,
  },
  {
    id: 'active',
    label: t('dashboard.activeTasks'),
    value: String(cardStat.value?.numActive ?? 0),
    icon: Play,
    accent: 'orange' as const,
    trend: [] as number[],
  },
  {
    id: 'waiting',
    label: t('dashboard.waitingTasks'),
    value: String(cardStat.value?.numWaiting ?? 0),
    icon: Pause,
    accent: 'purple' as const,
    hint: (cardStat.value?.numPaused ?? 0) > 0
      ? t('dashboard.pausedHint', { n: cardStat.value?.numPaused ?? 0 })
      : undefined,
    trend: [] as number[],
  },
  {
    id: 'completed',
    // v3.13.58: 「已停止任务」→「已完成任务」 — stopped 桶混装完成/失败/取消,
    // 用户看到成功下载躺在"已停止"里误以为失败。这里只统计真正完成的。
    label: t('dashboard.completedTasks'),
    value: String(completedCount.value),
    icon: CheckCircle2,
    accent: 'green' as const,
    trend: [] as number[],
  },
])

// --- recent completed -------------------------------------------------------
const completed = ref<CompletedTask[]>([])
const completedLoading = ref(true)
/** 已完成任务总数 — 「已完成任务」统计卡 (v3.13.58, 替代原「已停止任务」)。 */
const completedCount = ref(0)
/**
 * The backend's stopped bucket carries no completion timestamp, so the first
 * moment we observe a gid is recorded locally and used for the relative label.
 */
const completedAt = ref<Record<string, number>>({})
let completedTimer: number | null = null

async function loadCompleted(): Promise<void> {
  try {
    const list = await listRecentCompleted(5)
    const now = Date.now()
    const seen = { ...completedAt.value }
    for (const task of list) {
      if (!seen[task.gid]) seen[task.gid] = now
    }
    completedAt.value = seen
    completed.value = list
    completedCount.value = await countCompletedTasks()
  } catch {
    /* toast already shown */
  } finally {
    completedLoading.value = false
  }
}

// --- recent errors ---------------------------------------------------------
const errors = ref<CompletedTask[]>([])
const errorsLoading = ref(true)
/** Same local-timestamp trick as completedAt, keyed by gid. */
const failedAt = ref<Record<string, number>>({})
let errorsTimer: number | null = null

async function loadErrors(): Promise<void> {
  try {
    const list = await listRecentErrors(5)
    const now = Date.now()
    const seen = { ...failedAt.value }
    for (const task of list) {
      if (!seen[task.gid]) seen[task.gid] = now
    }
    failedAt.value = seen
    errors.value = list
  } catch {
    /* toast already shown */
  } finally {
    errorsLoading.value = false
  }
}

/** A finished download frees an active slot — refresh both lists when that happens. */
watch(
  () => cardStat.value?.numStopped,
  (next, prev) => {
    if (prev !== undefined && next !== undefined && next !== prev) {
      void loadCompleted()
      void loadErrors()
    }
  },
)

// --- cluster ----------------------------------------------------------------
const cluster = ref<ClusterSummary | null>(null)
const clusterLoading = ref(true)
// 集群摘要轮询 — 原先只在挂载时拉一次, 对方配对入群/删除后仪表盘的节点数
// 永远停在旧值, 只能整页刷新才更新 ("以为我这边有问题")。集群页节点表是
// 5s 一刷, 这里对齐同一节奏。
let clusterTimer: number | null = null

async function loadCluster(): Promise<void> {
  try {
    cluster.value = await getClusterSummary()
  } catch {
    // 轮询期间的瞬时失败保留上一次的值 — 覆盖成 null 会让卡片闪回
    // "集群未启用" 状态 (loadBTStats 同语义)。
  } finally {
    clusterLoading.value = false
  }
}

// --- BT lifetime stats (system.getBTStats) ---------------------------------
const btStats = ref<BTStats | null>(null)
const btStatsLoading = ref(true)
const btStatsError = ref<string | null>(null)
let btStatsTimer: number | null = null

async function loadBTStats(): Promise<void> {
  try {
    btStats.value = await getBTStats()
    btStatsError.value = null
  } catch (err) {
    // Keep the previous value but surface the failure inline.
    btStatsError.value = (err as Error)?.message ?? 'load failed'
  } finally {
    btStatsLoading.value = false
  }
}

const btRatio = computed<number>(() => {
  if (!btStats.value) return 0
  const dl = btStats.value.totalDownloaded
  if (dl <= 0) return 0
  return btStats.value.totalUploaded / dl
})

function formatRatio(r: number): string {
  if (!Number.isFinite(r) || r <= 0) return '0.00'
  if (r >= 100) return r.toFixed(0)
  return r.toFixed(2)
}

function formatUpdatedAt(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—'
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(seconds * 1000))
  } catch {
    return new Date(seconds * 1000).toISOString().slice(0, 16).replace('T', ' ')
  }
}

// --- manual refresh ---------------------------------------------------------
const refreshing = ref(false)

async function refreshAll(): Promise<void> {
  refreshing.value = true
  try {
    await Promise.all([
      loadStat(),
      loadCompleted(),
      loadErrors(),
      loadCluster(),
      loadBTStats(),
      refreshSpeed(),
    ])
  } finally {
    refreshing.value = false
  }
}

const connectionVariant = computed(() => {
  if (paused.value) return 'warning' as const
  return wsConnected.value ? 'success' as const : 'neutral' as const
})

const connectionLabel = computed(() => {
  if (paused.value) return t('dashboard.paused')
  return wsConnected.value ? t('dashboard.liveConnected') : t('dashboard.polling')
})

function navigateToTask(gid: string): void {
  void router.push({ name: 'tasks', query: { gid } })
}

onMounted(() => {
  void loadStat()
  void loadCompleted()
  void loadErrors()
  void loadCluster()
  void loadBTStats()
  statTimer = window.setInterval(() => {
    if (!paused.value) void loadStat()
  }, 1500)
  completedTimer = window.setInterval(() => {
    if (!paused.value) void loadCompleted()
  }, 15_000)
  errorsTimer = window.setInterval(() => {
    if (!paused.value) void loadErrors()
  }, 15_000)
  clusterTimer = window.setInterval(() => {
    if (!paused.value) void loadCluster()
  }, 5000)
  // BT stats refresh every 30s — values change slowly.
  btStatsTimer = window.setInterval(() => {
    if (!paused.value) void loadBTStats()
  }, 30_000)
})

const stopAllTimers = (): void => {
  if (statTimer !== null) window.clearInterval(statTimer)
  if (completedTimer !== null) window.clearInterval(completedTimer)
  if (errorsTimer !== null) window.clearInterval(errorsTimer)
  if (clusterTimer !== null) window.clearInterval(clusterTimer)
  if (btStatsTimer !== null) window.clearInterval(btStatsTimer)
}

onBeforeUnmount(() => {
  stopAllTimers()
})

// v3.14.0 (UI 打磨): 登出/401 后停止仪表盘全部轮询 — 此前登出后 1.5s
// stat 轮询等继续打未授权请求 (静默 401)。speedHistory 的轮询/WS 由
// 组合函数内部监听 sessionCleared 自行停止。
onSessionCleared(stopAllTimers)
</script>

<template>
  <div class="space-y-6">
    <!-- header -->
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 class="text-lg font-semibold text-fg">{{ t('dashboard.overview') }}</h1>
        <p class="mt-0.5 text-sm text-fg-muted">{{ t('dashboard.subtitle') }}</p>
      </div>

      <div class="flex items-center gap-2">
        <Badge :variant="connectionVariant" dot size="md">{{ connectionLabel }}</Badge>
        <Button
          size="sm"
          @click="showAddDialog = true"
        >
          <template #icon><Plus :size="14" /></template>
          {{ t('tasks.add') }}
        </Button>
        <Button
          variant="ghost"
          size="md"
          icon-only
          :aria-label="paused ? t('dashboard.resumeLive') : t('dashboard.pauseLive')"
          :title="paused ? t('dashboard.resumeLive') : t('dashboard.pauseLive')"
          @click="toggle"
        >
          <template #icon>
            <component :is="paused ? Play : Pause" :size="16" />
          </template>
        </Button>
        <Button
          variant="ghost"
          size="md"
          icon-only
          :disabled="refreshing"
          :aria-label="t('common.refresh')"
          :title="t('common.refresh')"
          @click="refreshAll"
        >
          <template #icon>
            <RefreshCw :size="16" :class="refreshing ? 'animate-spin' : ''" />
          </template>
        </Button>
      </div>
    </div>

    <!-- stat cards — xl 4 列 / 2xl 5 列: 16px 基准后 5 列在 1440 宽会把标签挤截断 -->
    <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
      <StatsCard
        v-for="card in cards"
        :key="card.id"
        :label="card.label"
        :value="card.value"
        :icon="card.icon"
        :accent="card.accent"
        :hint="card.hint"
        :trend="card.trend"
        :loading="statLoading && !cardStat"
        :clickable="card.id === 'completed'"
        @activate="
          card.id === 'completed' && router.push({ path: '/tasks', query: { tab: 'complete' } })
        "
      />
    </div>

    <!-- BT lifetime traffic (system.getBTStats) -->
    <Card
      :title="t('dashboard.btCard.title')"
      :subtitle="t('dashboard.btCard.subtitle')"
    >
      <template #actions>
        <div class="flex items-center gap-2">
          <Activity :size="14" class="text-fg-muted" />
          <span class="text-xs text-fg-muted">{{ formatRatio(btRatio) }}</span>
        </div>
      </template>

      <div v-if="btStatsError" class="rounded-md border border-danger/40 bg-danger/10 p-3 text-xs text-danger">
        {{ t('dashboard.btCard.loadError') }}: {{ btStatsError }}
      </div>

      <div
        v-else
        class="grid gap-4 sm:grid-cols-2"
      >
        <div class="rounded-md border border-border bg-bg-elevated px-4 py-3">
          <div class="flex items-center gap-2 text-xs text-fg-muted">
            <ArrowDownToLine :size="14" />
            <span>{{ t('dashboard.btCard.downloaded') }}</span>
          </div>
          <p class="mt-1 text-xl font-semibold tabular-nums text-fg">
            {{ btStats ? formatBytes(btStats.totalDownloaded) : (btStatsLoading ? '…' : '0 B') }}
          </p>
        </div>
        <div class="rounded-md border border-border bg-bg-elevated px-4 py-3">
          <div class="flex items-center gap-2 text-xs text-fg-muted">
            <ArrowUpFromLine :size="14" />
            <span>{{ t('dashboard.btCard.uploaded') }}</span>
          </div>
          <p class="mt-1 text-xl font-semibold tabular-nums text-fg">
            {{ btStats ? formatBytes(btStats.totalUploaded) : (btStatsLoading ? '…' : '0 B') }}
          </p>
        </div>
      </div>

      <p
        v-if="btStats && btStats.updatedAt > 0"
        class="mt-3 text-xs text-fg-subtle"
      >
        {{ t('dashboard.btCard.updatedAt', { time: formatUpdatedAt(btStats.updatedAt) }) }}
      </p>
    </Card>

    <!-- recent completed + recent errors (top summary) -->
    <div class="grid gap-6 lg:grid-cols-2">
      <Card :title="t('dashboard.recentCompleted')" :subtitle="t('dashboard.recentCompletedHint')">
        <RecentCompleted
          :items="completed"
          :loading="completedLoading"
          :completed-at="completedAt"
          @select="(task) => navigateToTask(task.gid)"
        />
      </Card>

      <Card :title="t('dashboard.recentErrorsCard')" :subtitle="t('dashboard.recentErrorsCardHint')">
        <RecentErrors
          :items="errors"
          :loading="errorsLoading"
          :failed-at="failedAt"
          @select="(task) => navigateToTask(task.gid)"
        />
      </Card>
    </div>

    <!-- system health (public /health + /api/version + AFD features) -->
    <HealthCard />

    <!-- speed chart -->
    <Card :title="t('dashboard.speedChart')" :subtitle="t('dashboard.speedChartHint')">
      <template #actions>
        <div class="flex items-center gap-3 text-xs text-fg-muted">
          <span class="flex items-center gap-1.5">
            <span class="h-2 w-2 rounded-full bg-brand-500" />
            {{ t('dashboard.downloadSpeed') }}
          </span>
          <span class="flex items-center gap-1.5">
            <span class="h-2 w-2 rounded-full bg-success" />
            {{ t('dashboard.uploadSpeed') }}
          </span>
        </div>
      </template>

      <SpeedChart
        :data="history"
        :height="280"
        :down-label="t('dashboard.downloadSpeed')"
        :up-label="t('dashboard.uploadSpeed')"
      />
    </Card>

    <!-- cluster status (full width — the disk bar wants the room) -->
    <ClusterStatusCard :summary="cluster" :loading="clusterLoading" />

    <!-- Add task from the dashboard home page -->
    <AddTaskDialog v-model="showAddDialog" @added="refreshAll" />
  </div>
</template>

